import React from "react";
import styles from "./Pagination.module.css";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export const ELLIPSIS = "...";

// Page counts here are driven by how much history a record happens to have, not by anything
// bounded — Investigate Phone Number reaches ~200 pages for a well-scrubbed number. Rendering
// one button per page turned that into a wall of controls nobody can aim at, so show only the
// ends and a window around wherever the reader currently is.
const WINDOW = 1;

// (number | typeof ELLIPSIS), not (number | string): comparing against the ELLIPSIS literal
// narrows a union of literals, but a bare `string` in the union survives that comparison, so the
// page branch would still be typed string | number and could not be handed to onPageChange.
export const pageWindow = (
  currentPage: number,
  totalPages: number,
): (number | typeof ELLIPSIS)[] => {
  // Below this every page fits without a gap, so the ellipsis logic never has to run.
  if (totalPages <= 2 * WINDOW + 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const first = 1;
  const last = totalPages;
  const from = Math.max(first + 1, currentPage - WINDOW);
  const to = Math.min(last - 1, currentPage + WINDOW);

  const pages: (number | typeof ELLIPSIS)[] = [first];
  if (from > first + 1) pages.push(ELLIPSIS);
  for (let p = from; p <= to; p++) pages.push(p);
  if (to < last - 1) pages.push(ELLIPSIS);
  pages.push(last);

  return pages;
};

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  const pageNumbers = pageWindow(currentPage, totalPages);

  return (
    <nav
      aria-label="Page navigation"
      className={styles["pagination-container"]}
    >
      <ul className="pagination justify-content-center flex-wrap">
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button
            className="page-link"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
        </li>
        {pageNumbers.map((page, index) =>
          page === ELLIPSIS ? (
            <li key={`gap-${index}`} className="page-item disabled">
              <span className="page-link">&hellip;</span>
            </li>
          ) : (
            <li
              key={page}
              className={`page-item ${currentPage === page ? "active" : ""}`}
            >
              <button className="page-link" onClick={() => onPageChange(page)}>
                {page}
              </button>
            </li>
          ),
        )}
        <li
          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination;
